/**
 * atomberg-cart-state.js
 * ──────────────────────
 * Shared /cart.js reader. Several scripts (sticky ATC, GTM + MoEngage
 * datalayers, installation service, discount cleanup) each re-fetch the cart
 * on every cart:update — five identical requests per add-to-cart, all inside
 * the interaction's critical path. This module dedupes concurrent readers
 * into a single request.
 *
 * API (window.AtombergCartState):
 *   getCart()              → Promise<cart> — cached until the next cart:update
 *   getCart({fresh: true}) → Promise<cart> — always current-generation data;
 *                            concurrent fresh callers share one request
 *
 * Every consumer keeps a direct-fetch fallback, so load order can never
 * break cart reads if this file is missing or fails.
 */
(function () {
  'use strict';

  /** @type {any} */
  var w = window;
  if (w.AtombergCartState) return;

  /** @type {any} */
  var cached = null;
  /** @type {Promise<any>|null} */
  var inflight = null;
  var generation = 0;
  var inflightGeneration = -1;

  /** @returns {Promise<any>} */
  function requestCart() {
    var gen = generation;
    /** @type {Promise<any>} */
    var p = fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        if (generation === gen) cached = cart;
        if (inflight === p) inflight = null;
        return cart;
      })
      .catch(function (err) {
        if (inflight === p) inflight = null;
        throw err;
      });
    inflight = p;
    inflightGeneration = gen;
    return p;
  }

  /**
   * @param {{fresh?: boolean}} [opts]
   * @returns {Promise<any>}
   */
  function getCart(opts) {
    var fresh = !!(opts && opts.fresh);
    if (!fresh) {
      if (cached) return Promise.resolve(cached);
      if (inflight) return inflight;
      return requestCart();
    }
    // fresh: share only a request started after the latest invalidation, so a
    // response that predates the cart mutation is never handed to a consumer
    // reacting to that mutation.
    if (inflight && inflightGeneration === generation) return inflight;
    return requestCart();
  }

  // Every cart:update dispatch in this theme targets `document` directly, so a
  // capture-phase listener on `document` itself would fire in plain script
  // registration order — no different from a bubble listener — and a
  // consumer whose script loads earlier (e.g. atomberg-sticky-atc.js, loaded
  // from product-information.liquid before this file) could read a fresh
  // request before this invalidation had run. Registering on `window`
  // instead guarantees this always runs first: capture propagates window →
  // document, so it fires ahead of every document-level listener regardless
  // of load order.
  window.addEventListener(
    'cart:update',
    function () {
      generation += 1;
      cached = null;
    },
    true
  );

  w.AtombergCartState = { getCart: getCart };
})();
